import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Swords, Trophy, Flame, Zap, Star, ArrowLeft, Loader2, Users, HelpCircle, Gem, Sparkles, Volume2, Gift, Coins, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import InteractiveBattle from '../components/battle/InteractiveBattle';
import { useAuth } from '../providers/AuthProvider';
import { useGameWallet } from '../providers/GameWalletProvider';
import { usePriceOracle } from '../providers/PriceOracleProvider';
import { transferToTreasury, checkSlapBalance } from '../services/tokenTransferService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================
// ============================================================
// FAKE WALLET ADDRESSES - Makes opponents look like real players
// ============================================================
const generateFakeWalletAddress = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz123456789';
  const start = Array.from({length: 4}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  const end = Array.from({length: 4}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${start}...${end}`;
};

const getRandomPlayerName = () => {
  // Return fake wallet address instead of username
  return generateFakeWalletAddress();
};

// Card class configuration
const CLASS_CONFIG = {
  meme: { emoji: '🐸', color: '#00FF99', name: 'MEME', beats: 'tech' },
  tech: { emoji: '🤖', color: '#00CCFF', name: 'TECH', beats: 'politics' },
  politics: { emoji: '🏛️', color: '#FF6B6B', name: 'POLITICS', beats: 'meme' },
};

// ============================================================
// STARTER HAND - Always available cards
// ============================================================
const STARTER_HAND = [
  { id: 'starter_elon', character_name: 'Elon Musk', title: 'Dogefather', rarity: 'legendary', power_rating: 5, card_class: 'tech', card_set: 'tech_moguls' },
  { id: 'starter_trump', character_name: 'Donald Trump', title: 'Deal Maker', rarity: 'legendary', power_rating: 4, card_class: 'politics', card_set: 'political_leaders' },
  { id: 'starter_vitalik', character_name: 'Vitalik Buterin', title: 'ETH Daddy', rarity: 'epic', power_rating: 4, card_class: 'meme', card_set: 'crypto_kings' },
  { id: 'starter_zuck', character_name: 'Mark Zuckerberg', title: 'Metaverse Master', rarity: 'rare', power_rating: 3, card_class: 'tech', card_set: 'tech_moguls' },
  { id: 'starter_putin', character_name: 'Vladimir Putin', title: 'The Strongman', rarity: 'epic', power_rating: 4, card_class: 'politics', card_set: 'political_leaders' },
  { id: 'starter_sbf', character_name: 'Sam Bankman-Fried', title: 'FTX King', rarity: 'rare', power_rating: 2, card_class: 'meme', card_set: 'crypto_kings' },
];

// MYSTERY GLITCH CARD - Fallback when something fails
const MYSTERY_CARD = {
  id: 'mystery_glitch',
  character_name: '??? GLITCH ???',
  title: 'Data Corrupted',
  rarity: 'epic',
  power_rating: 3,
  card_class: 'meme',
  card_set: 'unknown',
  isGlitch: true
};

// Rarity colors
const RARITY_COLORS = {
  common: '#A0A0A0',
  rare: '#00CCFF',
  epic: '#FF00FF',
  legendary: '#FFD700',
};

// ============================================================
// SOUND & HAPTICS SYSTEM
// ============================================================
const playSelectSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.05);
    oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);
  } catch (e) {
    console.log('Audio not available');
  }
};

const triggerHaptic = () => {
  if (navigator.vibrate) {
    navigator.vibrate(50);
  }
};

// ============================================================
// HOLOGRAPHIC PAD COMPONENT
// ============================================================
const HolographicPad = ({ roundNumber, card, isActive }) => {
  const cardClass = card ? CLASS_CONFIG[card.card_class] || CLASS_CONFIG.meme : null;
  
  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: roundNumber * 0.1 }}
    >
      {/* Round Label */}
      <motion.p 
        className="font-bebas text-sm text-cyan-400 mb-2 text-center tracking-widest"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        ROUND {roundNumber}
      </motion.p>
      
      {/* Holographic Deployment Pad */}
      <motion.div
        className="relative w-32 h-44 rounded-xl overflow-hidden"
        animate={isActive ? {
          boxShadow: [
            '0 0 20px rgba(0,255,255,0.3), inset 0 0 30px rgba(0,255,255,0.1)',
            '0 0 40px rgba(0,255,255,0.6), inset 0 0 50px rgba(0,255,255,0.2)',
            '0 0 20px rgba(0,255,255,0.3), inset 0 0 30px rgba(0,255,255,0.1)',
          ]
        } : {}}
        transition={{ duration: 1.5, repeat: Infinity }}
        style={{
          background: card 
            ? `linear-gradient(135deg, ${cardClass?.color}40 0%, #0a0a0a 50%, ${cardClass?.color}20 100%)`
            : 'linear-gradient(135deg, rgba(0,255,255,0.1) 0%, #0a0a0a 50%, rgba(255,0,255,0.1) 100%)',
          border: card ? `3px solid ${cardClass?.color}` : '3px solid rgba(0,255,255,0.3)',
        }}
      >
        {/* Holographic Grid Lines */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `
              linear-gradient(0deg, rgba(0,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '10px 10px',
          }}
        />
        
        {/* Scanning Line Effect */}
        <motion.div
          className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
          animate={{ top: ['-10%', '110%'] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        />
        
        {card ? (
          /* Card Content */
          <div className="relative z-10 h-full flex flex-col">
            {/* Class Badge */}
            <div 
              className="absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-bold"
              style={{ background: `${cardClass?.color}60`, color: cardClass?.color }}
            >
              {cardClass?.emoji}
            </div>
            
            {/* Card Visual - Glitch effect for mystery cards */}
            <div className={`flex-1 flex items-center justify-center ${card.isGlitch ? 'animate-pulse' : ''}`}>
              {card.isGlitch ? (
                <motion.div
                  className="text-4xl"
                  animate={{ 
                    opacity: [1, 0.3, 1],
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  ❓
                </motion.div>
              ) : (
                <Sparkles className="w-12 h-12" style={{ color: cardClass?.color }} />
              )}
            </div>
            
            {/* Card Info */}
            <div className="p-2 bg-black/80">
              <p className="font-bebas text-xs text-white truncate text-center">{card.character_name}</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                <span className="text-xs font-bold" style={{ color: cardClass?.color }}>
                  {cardClass?.emoji} {card.power_rating}★
                </span>
              </div>
            </div>
          </div>
        ) : (
          /* Empty Pad - Waiting Animation */
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              className="w-16 h-16 rounded-full border-2 border-dashed border-cyan-500/50"
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute w-8 h-8 rounded-full border border-cyan-400/30"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// ============================================================
// MAIN ARENA COMPONENT
// ============================================================
const PvPArena = () => {
  const navigate = useNavigate();
  const { walletAddress, user, refreshUser, loading: authLoading, secretKey, solanaAddress, localWallet } = useAuth();
  const { slapBalance, fetchSlapTokenBalance, connectionStatus } = useGameWallet();
  const { tokenPrice, isLive } = usePriceOracle();
  
  const [userCards, setUserCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [availableBattles, setAvailableBattles] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false); // For RPC BUSY state
  const [showClassHelp, setShowClassHelp] = useState(false);
  const [claimingShards, setClaimingShards] = useState(false);
  
  // On-chain transaction state
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentProgress, setPaymentProgress] = useState(null);
  
  // Opponent state (realistic names)
  const [opponentName, setOpponentName] = useState(getRandomPlayerName());
  const [isSearchingOpponent, setIsSearchingOpponent] = useState(false);
  const [rematchStatus, setRematchStatus] = useState(null); // null, 'requesting', 'declined', 'searching'
  
  // PHASE 11: Arena Tier Selection
  const [selectedTier, setSelectedTier] = useState('fish'); // fish, shark, whale
  
  // ============================================================
  // PHASE 11: ARENA ECONOMICS - HOUSE PROFIT (NO BURN)
  // ============================================================
  // TREASURY WALLET: All fees & losses go here
  const TREASURY_WALLET = 'GntfPdLmhjJ2unKJ9fp7S6rDz7AA1Hkn52pKpd9TceCa';
  
  // ============================================================
  // 1v1 ARENA ECONOMICS - Winner Takes All (Pot = Entry x 2)
  // ============================================================
  // House Edge: Bot wins ~60% of time via power boost
  // Simple 1v1 gameplay
  // 
  // ALL LOST TOKENS GO TO TREASURY - NOT BURNED!
  // ============================================================
  
  // USD-FIXED ENTRY FEES - 3 ARENAS: FISH, SHARK, WHALE
  const USD_ENTRY_FEES = {
    fish: 1.00,       // 🐟 FISH - $1.00 USD (Beginner)
    shark: 5.00,      // 🦈 SHARK - $5.00 USD (Advanced)
    whale: 20.00      // 🐋 WHALE - $20.00 USD (High Roller)
  };
  
  // Calculate $SLAP amount from USD using live DexScreener price
  const calculateSlapFromUsd = useCallback((usdAmount) => {
    if (!tokenPrice || tokenPrice <= 0) return Math.ceil(usdAmount / 0.0001); // Fallback
    return Math.ceil(usdAmount / tokenPrice);
  }, [tokenPrice]);
  
  // ============================================================
  // HOUSE FEE - Treasury keeps 10% of every pot
  // ============================================================
  const HOUSE_FEE_PERCENT = 10; // 10% Hausgebühr
  
  // Calculate reward after house fee: (Entry x 2) - 10% = Entry x 1.8
  const calculateRewardAfterFee = useCallback((entryFeeUsd) => {
    const totalPot = calculateSlapFromUsd(entryFeeUsd) * 2; // Both players' entry
    const houseFee = Math.floor(totalPot * (HOUSE_FEE_PERCENT / 100));
    return totalPot - houseFee; // Winner gets pot minus house fee
  }, [calculateSlapFromUsd]);
  
  // ============================================================
  // 3 ARENAS: FISH 🐟, SHARK 🦈, WHALE 🐋
  // Dynamic costs recalculated from live token price
  // 1v1: Pot = Entry x 2, House takes 10%, Winner gets 90%
  // ============================================================
  const ARENA_TIERS = {
    fish: { 
      name: 'FISH', 
      subtitle: 'Beginner Pool',
      cost: calculateSlapFromUsd(USD_ENTRY_FEES.fish), 
      color: 'cyan',
      gradient: 'from-cyan-500 to-blue-500',
      borderColor: 'border-cyan-400',
      bgColor: 'bg-cyan-500/10',
      reward: calculateRewardAfterFee(USD_ENTRY_FEES.fish),
      houseFee: Math.floor(calculateSlapFromUsd(USD_ENTRY_FEES.fish) * 2 * 0.10),
      icon: '🐟', 
      usd: '$1.00',
      players: 2,
      description: 'Perfect for beginners. Low risk, learn the game!'
    },
    shark: { 
      name: 'SHARK', 
      subtitle: 'Advanced Waters',
      cost: calculateSlapFromUsd(USD_ENTRY_FEES.shark), 
      color: 'purple',
      gradient: 'from-purple-500 to-pink-500',
      borderColor: 'border-purple-400',
      bgColor: 'bg-purple-500/10',
      reward: calculateRewardAfterFee(USD_ENTRY_FEES.shark),
      houseFee: Math.floor(calculateSlapFromUsd(USD_ENTRY_FEES.shark) * 2 * 0.10),
      icon: '🦈', 
      usd: '$5.00',
      players: 2,
      description: 'For experienced players. Higher stakes!'
    },
    whale: { 
      name: 'WHALE', 
      subtitle: 'High Roller Ocean',
      cost: calculateSlapFromUsd(USD_ENTRY_FEES.whale), 
      color: 'yellow',
      gradient: 'from-yellow-500 to-orange-500',
      borderColor: 'border-yellow-400',
      bgColor: 'bg-yellow-500/10',
      reward: calculateRewardAfterFee(USD_ENTRY_FEES.whale),
      houseFee: Math.floor(calculateSlapFromUsd(USD_ENTRY_FEES.whale) * 2 * 0.10),
      icon: '🐋', 
      usd: '$20.00',
      players: 2,
      description: 'VIP Arena. Massive rewards for big players!'
    }
  };
  
  // House makes money from:
  // 1. 10% fee on every pot (guaranteed profit)
  // 2. Bot winning ~60% of matches (additional profit)
  
  // House edge boost range - INCREASED for 60% bot win rate
  const HOUSE_EDGE_MIN = 0.10; // 10% minimum power boost for bot
  const HOUSE_EDGE_MAX = 0.20; // 20% maximum power boost for bot
  
  // Battle state
  const [isMatchmaking, setIsMatchmaking] = useState(false);
  const [matchmakingStatus, setMatchmakingStatus] = useState(null);
  const [battleError, setBattleError] = useState(null);
  const matchmakingInterval = useRef(null);
  
  // Interactive battle state
  const [interactiveBattleData, setInteractiveBattleData] = useState(null);
  const [showInteractiveBattle, setShowInteractiveBattle] = useState(false);
  
  const SESSION_ID = walletAddress || 'default_session';
  
  // ============================================================
  // CRITICAL: SANITIZE BALANCE - Handle string/number type mismatch
  // slapBalance might be a formatted string like "15.000.000" or a number
  // We need a clean NUMBER for math comparisons
  // ============================================================
  const sanitizeBalance = (balance) => {
    if (typeof balance === 'number') return balance;
    if (typeof balance === 'string') {
      // Remove all dots and commas, then parse as float
      const cleaned = balance.replace(/[.,\s]/g, '');
      return parseFloat(cleaned) || 0;
    }
    return 0;
  };
  
  // CRITICAL FIX: Use BLOCKCHAIN balance (slapBalance), NOT server shards
  // This was the bug: userShards was using user?.shards (server data = 5000)
  // Now we use slapBalance (blockchain data = 15,000,000)
  // Use SANITIZED blockchain $SLAP balance for ALL math operations
  const userSlap = sanitizeBalance(slapBalance);

  // Claim free test shards
  const claimTestShards = async () => {
    setClaimingShards(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/user/claim-test-shards?session_id=${SESSION_ID}`);
      if (response.data.success) {
        toast.success(`+${response.data.shards_added} SHARDS!`, {
          description: `New balance: ${response.data.new_balance} Shards`
        });
        await refreshUser();
      }
    } catch (err) {
      toast.error('Failed to claim shards');
      console.error(err);
    } finally {
      setClaimingShards(false);
    }
  };

  // ============================================================
  // LOAD STARTER CARDS - Always available for gameplay
  // ============================================================
  const loadStarterCards = useCallback(() => {
    playSelectSound();
    triggerHaptic();
    setUserCards(STARTER_HAND);
    setCardsLoading(false);
    setSelectedCards([]);
  }, []);

  // Load real cards from DB with graceful fallback - NOW WITH LIVE POWER
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let isMounted = true;
    const matchmakingIntervalRef = matchmakingInterval.current;
    let timeoutId = null;
    
    const loadCards = async () => {
      if (!isMounted || authLoading) return;
      
      setCardsLoading(true);
      setLoadingTimeout(false);
      
      // Set 5 second timeout for RPC BUSY state
      timeoutId = setTimeout(() => {
        if (isMounted && cardsLoading) {
          setLoadingTimeout(true);
        }
      }, 5000);
      
      try {
        // Try to get cards with live power data first
        let response;
        try {
          response = await axios.get(`${API}/cards/collection/${SESSION_ID}/with-live-power`, {
            timeout: 5000
          });
        } catch (e) {
          // Fallback to regular collection endpoint
          response = await axios.get(`${API}/cards/collection?session_id=${SESSION_ID}`, {
            timeout: 3000
          });
        }
        
        if (!isMounted) return;
        
        let cards = (response.data.cards || []).filter(c => !c.is_listed);
        
        // Filter out destroyed cards (integrity 0)
        cards = cards.filter(c => (c.integrity ?? 3) > 0);
        
        // If no cards, use starter hand
        if (cards.length === 0) {
          loadStarterCards();
          return;
        }
        
        // Ensure all cards have required fields, replace broken ones with Mystery Card
        cards = cards.map(card => {
          if (!card.id || !card.character_name) {
            return { ...MYSTERY_CARD, id: `mystery_${Math.random()}` };
          }
          // Add default live power values if not present
          return {
            ...card,
            integrity: card.integrity ?? 3,
            live_stars: card.live_stars ?? card.power_rating,
            score_multiplier: card.score_multiplier ?? 1.0,
            daily_power_label: card.daily_power_label ?? '⚡ +0%'
          };
        });
        
        setUserCards(cards);
      } catch (error) {
        console.log('DB unavailable, loading starter cards');
        if (isMounted) {
          loadStarterCards();
        }
      } finally {
        if (isMounted) {
          setCardsLoading(false);
          setLoadingTimeout(false);
          clearTimeout(timeoutId);
        }
      }
    };
    
    if (!authLoading) {
      loadCards();
      fetchAvailableBattles();
    }
    
    const interval = setInterval(fetchAvailableBattles, 5000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
      clearTimeout(timeoutId);
      if (matchmakingIntervalRef) clearInterval(matchmakingIntervalRef);
    };
  }, [SESSION_ID, authLoading, loadStarterCards]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchAvailableBattles = async () => {
    try {
      const response = await axios.get(`${API}/pvp/available-battles`, { timeout: 3000 });
      setAvailableBattles(response.data.battles || []);
    } catch (error) {
      // Silent fail - not critical
    }
  };

  // ============================================================
  // CARD SELECTION WITH SOUND & HAPTICS
  // ============================================================
  const handleCardSelect = (card) => {
    playSelectSound();
    triggerHaptic();
    
    if (selectedCards.find(c => c.id === card.id)) {
      setSelectedCards(selectedCards.filter(c => c.id !== card.id));
    } else if (selectedCards.length < 3) {
      setSelectedCards([...selectedCards, card]);
      
      if (selectedCards.length === 2) {
        // Third card selected - play special sound
        setTimeout(() => {
          playSelectSound();
          triggerHaptic();
          toast.success('🔥 BATTLE HAND READY!', { duration: 1500 });
        }, 100);
      }
    } else {
      toast.error('Maximum 3 cards!');
    }
  };

  // ============================================================
  // BATTLE HANDLER - REALISTIC MATCHMAKING WITH DELAYS
  // ============================================================
  const handleCpuBattle = async () => {
    if (selectedCards.length !== 3) {
      toast.error('Select exactly 3 cards!');
      return;
    }
    
    // Proceed directly to battle
    proceedWithBattle();
  };
  
  const proceedWithBattle = async () => {
    setBattleError(null);
    setIsMatchmaking(true);
    
    // Generate new realistic opponent name
    const newOpponent = getRandomPlayerName();
    setOpponentName(newOpponent);
    
    playSelectSound();
    triggerHaptic();
    
    const currentTier = ARENA_TIERS[selectedTier];
    const entryFeeSlap = currentTier.cost;
    
    // Debug log to trace the issue
    console.log('[PvPArena] Balance Check:', {
      rawSlapBalance: slapBalance,
      sanitizedUserSlap: userSlap,
      entryFeeSlap: entryFeeSlap,
      typeOfRaw: typeof slapBalance,
      comparison: userSlap < entryFeeSlap
    });
    
    // ============================================================
    // CRITICAL: REAL ON-CHAIN TOKEN TRANSFER TO TREASURY
    // Game ONLY starts after successful blockchain confirmation!
    // ============================================================
    
    // Get user's secret key for signing
    const userSecretKey = secretKey || localWallet?.secretKey;
    const userPublicKey = solanaAddress || localWallet?.publicKey;
    
    if (!userSecretKey || !userPublicKey) {
      toast.error('Wallet not connected!', {
        description: 'Please connect your wallet to play'
      });
      setIsMatchmaking(false);
      return;
    }
    
    // Step 1: Check if user has enough $SLAP (using sanitized balance)
    setMatchmakingStatus({ status: 'checking', message: 'Checking balance...' });
    setIsProcessingPayment(true);
    
    // First check locally with sanitized balance
    if (userSlap < entryFeeSlap) {
      toast.error(`Insufficient $SLAP!`, {
        description: `Need ${entryFeeSlap.toLocaleString()} $SLAP, have ${Math.floor(userSlap).toLocaleString()}`
      });
      setIsMatchmaking(false);
      setIsProcessingPayment(false);
      return;
    }
    
    const balanceCheck = await checkSlapBalance(userPublicKey, entryFeeSlap);
    if (!balanceCheck.hasEnough) {
      toast.error(`Insufficient $SLAP!`, {
        description: `Need ${entryFeeSlap.toLocaleString()} $SLAP, have ${Math.floor(balanceCheck.balance).toLocaleString()}`
      });
      setIsMatchmaking(false);
      setIsProcessingPayment(false);
      return;
    }
    
    // Step 2: Execute REAL on-chain token transfer to Treasury
    setMatchmakingStatus({ status: 'paying', message: `Transferring ${entryFeeSlap} $SLAP to Treasury...` });
    
    console.log(`[PvPArena] 💰 REAL TRANSFER: ${entryFeeSlap} $SLAP → Treasury: ${TREASURY_WALLET}`);
    
    const transferResult = await transferToTreasury({
      amount: entryFeeSlap,
      userPublicKey: userPublicKey,
      secretKeyBase58: userSecretKey,
      onProgress: (progress) => {
        setPaymentProgress(progress);
        setMatchmakingStatus({ 
          status: 'paying', 
          message: progress.message 
        });
      }
    });
    
    setIsProcessingPayment(false);
    setPaymentProgress(null);
    
    if (!transferResult.success) {
      toast.error('Entry Fee Transfer Failed!', {
        description: transferResult.error || 'Transaction rejected'
      });
      setIsMatchmaking(false);
      return;
    }
    
    // ✅ Transaction confirmed! Now start the game
    console.log(`[PvPArena] ✅ CONFIRMED: ${entryFeeSlap} $SLAP → Treasury. Tx: ${transferResult.signature}`);
    
    toast.success('Entry Fee Paid! ✅', {
      description: `${entryFeeSlap} $SLAP → Treasury (${transferResult.signature?.slice(0, 8)}...)`,
      duration: 3000
    });
    
    // Refresh balance after transfer
    await fetchSlapTokenBalance(false);

    // Realistic matchmaking simulation with delays
    setMatchmakingStatus({ status: 'searching', message: `Searching for opponent in ${currentTier.name}...` });
    await new Promise(r => setTimeout(r, 1500 + Math.random() * 1000));
    
    setMatchmakingStatus({ status: 'found', message: `Found: ${newOpponent}` });
    await new Promise(r => setTimeout(r, 800));
    
    setMatchmakingStatus({ status: 'preparing', message: 'Preparing battle...' });
    await new Promise(r => setTimeout(r, 600));

    // PHASE 11: House edge - Bot gets aggressive power boost (10-20%)
    // 1v1: Winner takes pot (Entry x 2)
    const houseEdgeBoost = 1 + (Math.random() * (HOUSE_EDGE_MAX - HOUSE_EDGE_MIN) + HOUSE_EDGE_MIN);
    
    const botCards = STARTER_HAND.slice(0, 3).map((c, i) => ({
      ...c,
      id: `opponent_${i}`,
      character_name: [`${newOpponent}'s Ace`, `${newOpponent}'s Fighter`, `${newOpponent}'s Tank`][i],
      power_rating: Math.min(5, Math.ceil((Math.floor(Math.random() * 3) + 2) * houseEdgeBoost)),
    }));
    
    setInteractiveBattleData({
      battleId: `match_${Date.now()}`,
      playerCards: selectedCards,
      botCards: botCards,
      tier: selectedTier,
      entryCost: currentTier.cost,
      winReward: currentTier.reward,
      isHouseMatch: true,
      opponentName: newOpponent,
      entryTxSignature: transferResult.signature // Store the transaction signature
    });
    setShowInteractiveBattle(true);
    setIsMatchmaking(false);
  };

  // ============================================================
  // REMATCH LOGIC - Realistic with decline chance
  // ============================================================
  const handlePlayAgain = async () => {
    setRematchStatus('requesting');
    
    // Show requesting animation
    toast.loading('Requesting rematch...', { id: 'rematch' });
    
    // Random delay 2-4 seconds
    await new Promise(r => setTimeout(r, 2000 + Math.random() * 2000));
    
    // 80% chance opponent accepts, 20% declines
    if (Math.random() < 0.8) {
      toast.success(`${opponentName} accepted!`, { id: 'rematch' });
      setRematchStatus(null);
      // Reset for new game
      setSelectedCards([]);
    } else {
      // Opponent declined
      toast.error(`${opponentName} left the room`, { id: 'rematch' });
      setRematchStatus('searching');
      
      // Search for new opponent
      await new Promise(r => setTimeout(r, 1500));
      const newOpponent = getRandomPlayerName();
      setOpponentName(newOpponent);
      toast.success(`Found: ${newOpponent}`, { id: 'rematch' });
      setRematchStatus(null);
      setSelectedCards([]);
    }
  };

  const handleBattleComplete = async (result, playerWon, damageData = null) => {
    setShowInteractiveBattle(false);
    const battleData = interactiveBattleData;
    setInteractiveBattleData(null);
    setSelectedCards([]);
    
    const tierReward = battleData?.winReward || ARENA_TIERS[selectedTier].reward;
    const tierCost = battleData?.entryCost || ARENA_TIERS[selectedTier].cost;
    
    await refreshUser();
    
    // Reload cards to get updated integrity values
    try {
      const response = await axios.get(`${API}/cards/collection/${SESSION_ID}/with-live-power`, {
        timeout: 3000
        });
        let cards = (response.data.cards || []).filter(c => !c.is_listed && (c.integrity ?? 3) > 0);
        setUserCards(cards);
      } catch (e) {
        console.log('Failed to reload cards');
      }
    
    if (playerWon) {
      // Player wins: 1v1 pot (their entry + opponent's entry)
      console.log(`[Arena] 🏆 Player WIN: +${tierReward} $SLAP (Winner takes pot)`);
      console.log(`[Arena] Treasury: ${TREASURY_WALLET}`);
      
      toast.success(`🏆 VICTORY!`, {
        description: `+${tierReward} $SLAP - Winner takes the pot!`,
        duration: 5000
      });
    } else {
      // Player loses: Entry fee goes to house
      console.log(`[Arena] 💰 House WIN: ${tierCost} $SLAP → Treasury: ${TREASURY_WALLET}`);
      
      toast.error(`😢 YOU LOST`, {
        description: `${tierCost} $SLAP → Treasury`,
        duration: 5000
      });
      
      // Show damage/destruction toasts
      if (damageData) {
        if (damageData.cards_destroyed?.length > 0) {
          damageData.cards_destroyed.forEach(card => {
            toast.error(`💀 ${card.card_name} DESTROYED!`, { duration: 8000 });
          });
        }
        if (damageData.cards_damaged?.length > 0) {
          damageData.cards_damaged.forEach(card => {
            toast.warning(`⚠️ ${card.card_name} cracked! (${card.new_integrity}/3)`, { duration: 5000 });
          });
        }
      }
      toast.error(`💀 DEFEAT!`, {
        description: `Lost ${tierCost} $SLAP → House Treasury`,
        duration: 5000
      });
    }
  };

  const cancelMatchmaking = () => {
    if (matchmakingInterval.current) clearInterval(matchmakingInterval.current);
    setIsMatchmaking(false);
    setMatchmakingStatus(null);
    setBattleError(null);
    setRematchStatus(null);
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden pb-20">
      {/* Background Effects */}
      <div 
        className="fixed inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#FF000010 1px, transparent 1px),
            linear-gradient(90deg, #FF000010 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-red-600/10 rounded-full blur-[150px] pointer-events-none" />

      {/* Interactive Battle Overlay */}
      <AnimatePresence>
        {showInteractiveBattle && interactiveBattleData && (
          <InteractiveBattle 
            playerCards={interactiveBattleData.playerCards}
            opponentCards={interactiveBattleData.botCards}
            onBattleComplete={handleBattleComplete}
            sessionId={SESSION_ID}
          />
        )}
      </AnimatePresence>

      {/* Class Help Modal */}
      <AnimatePresence>
        {showClassHelp && (
          <motion.div
            className="fixed inset-0 z-[900] flex items-center justify-center bg-black/90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowClassHelp(false)}
          >
            <motion.div
              className="bg-[#0a0a0a] border-2 border-neon-pink rounded-2xl p-8 max-w-lg mx-4"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="font-anton text-3xl text-white mb-6 text-center">
                ⚔️ TACTICAL BATTLE SYSTEM
              </h2>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {Object.entries(CLASS_CONFIG).map(([key, config]) => (
                  <div 
                    key={key}
                    className="p-3 rounded-lg text-center"
                    style={{ background: `${config.color}20`, border: `2px solid ${config.color}` }}
                  >
                    <span className="text-3xl">{config.emoji}</span>
                    <p className="font-anton text-sm mt-1" style={{ color: config.color }}>{config.name}</p>
                  </div>
                ))}
              </div>
              <div className="bg-white/10 rounded-lg p-4 mb-4">
                <p className="font-space text-sm text-[#00FF99] mb-2">🐸 MEME beats 🤖 TECH (+2 stars)</p>
                <p className="font-space text-sm text-[#00CCFF] mb-2">🤖 TECH beats 🏛️ POLITICS (+2 stars)</p>
                <p className="font-space text-sm text-[#FF6B6B]">🏛️ POLITICS beats 🐸 MEME (+2 stars)</p>
              </div>
              <Button onClick={() => setShowClassHelp(false)} className="w-full bg-neon-pink hover:bg-neon-pink/80 font-anton">
                GOT IT!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="relative z-50 pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4">
            <Button
              onClick={() => navigate('/')}
              className="bg-transparent border-2 border-white/20 text-white hover:border-white hover:bg-white/10 font-space uppercase"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            
            {/* $SLAP Balance - Shows "---" when loading, NEVER fake values */}
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-black/60 border-2 border-yellow-500/50">
              <Coins className="w-5 h-5 text-yellow-400" />
              <span className="font-anton text-xl text-yellow-400" data-testid="slap-balance">
                {connectionStatus === 'connecting' ? '---' : `$${userSlap.toLocaleString()}`}
              </span>
              <span className="font-space text-xs text-white/60">$SLAP</span>
            </div>
          </div>

          <motion.h1
            className="font-anton text-5xl md:text-7xl uppercase tracking-tight text-center"
            style={{
              background: 'linear-gradient(180deg, #FF0000 0%, #FF00FF 50%, #FFD700 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="arena-title"
          >
            <Swords className="inline w-12 h-12 mr-4 text-red-500" />
            TACTICAL ARENA
            <Flame className="inline w-12 h-12 ml-4 text-orange-500" />
          </motion.h1>
          <p className="font-space text-base text-white/60 text-center mt-4 tracking-wider">
            BEST OF 3 ROUNDS • ROCK-PAPER-SCISSORS STRATEGY • WIN $SLAP
          </p>
          
          <div className="flex justify-center mt-4">
            <Button
              onClick={() => setShowClassHelp(true)}
              className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-space text-sm"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              How Card Classes Work
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* CONNECTION ERROR STATE - No backup UI, just error + retry */}
          {connectionStatus === 'error' && (
            <motion.div
              className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="text-center max-w-md mx-4">
                <div className="text-6xl mb-6">🔴</div>
                <h2 className="font-anton text-3xl text-red-400 mb-4">CONNECTION ERROR</h2>
                <p className="font-space text-white/70 mb-6">
                  Unable to connect to Solana network. Please check your connection and try again.
                </p>
                <Button
                  onClick={() => window.location.reload()}
                  className="px-8 py-4 bg-red-600 hover:bg-red-500 text-white font-anton text-xl rounded-xl"
                >
                  🔄 RETRY CONNECTION
                </Button>
                <p className="mt-4 text-white/40 text-sm">
                  Auto-retrying in background...
                </p>
              </div>
            </motion.div>
          )}

          {/* Card Selection - BIGGER CARDS (30% increase) */}
          <div className="mb-8">
            <h2 className="font-anton text-2xl text-white mb-4 flex items-center gap-3">
              <Volume2 className="w-5 h-5 text-cyan-400" />
              SELECT YOUR BATTLE HAND ({selectedCards.length}/3)
            </h2>
            
            {cardsLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-neon-pink animate-spin mr-3" />
                {loadingTimeout ? (
                  <div className="text-center mt-4">
                    <p className="font-anton text-xl text-red-400 mb-3">⚠️ RPC BUSY</p>
                    <button
                      onClick={() => window.location.reload()}
                      className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-lg"
                    >
                      🔄 RETRY
                    </button>
                  </div>
                ) : (
                  <span className="font-space text-white/60">Loading cards...</span>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {userCards.slice(0, 10).map((card) => {
                  const cardClass = CLASS_CONFIG[card.card_class] || CLASS_CONFIG.meme;
                  const isSelected = selectedCards.find(c => c.id === card.id);
                  const selectionIndex = selectedCards.findIndex(c => c.id === card.id);
                  const rarityColor = RARITY_COLORS[card.rarity] || RARITY_COLORS.common;
                  
                  return (
                    <motion.div
                      key={card.id}
                      onClick={() => handleCardSelect(card)}
                      className={`relative cursor-pointer rounded-xl overflow-hidden transition-all ${
                        isSelected
                          ? 'ring-4 ring-yellow-400 shadow-[0_0_30px_rgba(255,215,0,0.7)]'
                          : 'hover:ring-2 hover:ring-white/40'
                      }`}
                      style={{
                        background: `linear-gradient(135deg, ${cardClass.color}20 0%, #0a0a0a 50%, ${rarityColor}20 100%)`,
                        border: `3px solid ${isSelected ? '#FFD700' : cardClass.color}`,
                      }}
                      whileHover={{ scale: 1.05, y: -5 }}
                      whileTap={{ scale: 0.98 }}
                      layout
                    >
                      {/* Selection Badge */}
                      {isSelected && (
                        <motion.div 
                          className="absolute top-3 left-3 z-20 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg"
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 500 }}
                        >
                          <span className="font-anton text-xl text-black">{selectionIndex + 1}</span>
                        </motion.div>
                      )}
                      
                      {/* Rarity Badge */}
                      <div 
                        className="absolute top-3 right-3 z-20 px-2 py-1 rounded text-xs font-bold uppercase"
                        style={{ background: `${rarityColor}40`, color: rarityColor, border: `1px solid ${rarityColor}` }}
                      >
                        {card.rarity}
                      </div>
                      
                      {/* Class Badge */}
                      <div 
                        className="absolute bottom-16 right-3 z-20 px-2 py-1 rounded text-lg"
                        style={{ background: `${cardClass.color}60` }}
                      >
                        {cardClass.emoji}
                      </div>
                      
                      {/* Card Image Area - BIGGER */}
                      <div className="h-44 flex items-center justify-center bg-gradient-to-b from-black/20 to-black/60">
                        {card.image_base64 ? (
                          <img
                            src={`data:image/png;base64,${card.image_base64}`}
                            alt={card.character_name}
                            className="w-full h-full object-cover"
                          />
                        ) : card.isGlitch ? (
                          <motion.div
                            className="text-6xl"
                            animate={{ opacity: [1, 0.3, 1], rotate: [0, 5, -5, 0] }}
                            transition={{ duration: 0.5, repeat: Infinity }}
                          >
                            ❓
                          </motion.div>
                        ) : (
                          <div className="text-center p-4">
                            <Sparkles className="w-16 h-16 mx-auto mb-2" style={{ color: cardClass.color }} />
                            <p className="font-bebas text-sm text-white/40">NO IMAGE</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Card Info - BIGGER - WITH INTEGRITY & LIVE POWER */}
                      <div className="p-4 bg-black/80">
                        <p className="font-bebas text-lg text-white truncate">{card.character_name}</p>
                        <p className="font-space text-xs text-white/50 truncate">{card.title}</p>
                        
                        {/* Live Power Badge */}
                        {card.daily_power_label && (
                          <div className={`mt-1 px-2 py-0.5 rounded text-xs font-bold text-center ${
                            card.daily_power_label.includes('🔥') ? 'bg-orange-500/30 text-orange-300' :
                            card.daily_power_label.includes('❄️') ? 'bg-blue-500/30 text-blue-300' :
                            'bg-yellow-500/30 text-yellow-300'
                          }`}>
                            POWER: {card.daily_power_label}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${i < (card.live_stars || card.power_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`}
                              />
                            ))}
                          </div>
                          
                          {/* Integrity Indicator */}
                          <div className="flex items-center gap-1">
                            {[...Array(3)].map((_, i) => (
                              <div
                                key={i}
                                className={`w-2 h-4 rounded-sm ${
                                  i < (card.integrity ?? 3) 
                                    ? (card.integrity ?? 3) === 1 ? 'bg-red-500' : 'bg-green-500'
                                    : 'bg-gray-700'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* HOLOGRAPHIC DEPLOYMENT PADS */}
          {selectedCards.length > 0 && (
            <motion.div 
              className="bg-black/60 border-2 border-cyan-500/30 rounded-2xl p-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'linear-gradient(180deg, rgba(0,255,255,0.05) 0%, rgba(0,0,0,0.8) 50%, rgba(255,0,255,0.05) 100%)',
              }}
            >
              <h3 className="font-anton text-xl text-cyan-400 mb-6 text-center tracking-widest">
                ⚡ DEPLOYMENT ZONE ⚡
              </h3>
              <div className="flex justify-center gap-8">
                {[1, 2, 3].map((round) => (
                  <HolographicPad
                    key={round}
                    roundNumber={round}
                    card={selectedCards[round - 1] || null}
                    isActive={selectedCards.length >= round}
                  />
                ))}
              </div>
              <p className="font-space text-xs text-white/40 text-center mt-4">
                Cards deploy in order. Choose wisely!
              </p>
            </motion.div>
          )}

          {/* ARENA TIER SELECTION - SIMPLE 3-CARD LAYOUT WITH USD PRICING */}
          {selectedCards.length === 3 && !isMatchmaking && (
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="font-anton text-3xl text-center text-white mb-2 tracking-widest">
                ⚔️ CHOOSE YOUR ARENA ⚔️
              </h2>
              <p className="text-center text-white/60 text-sm mb-6">
                1v1 Battle • Click to Start • Winner Takes All
              </p>
              
              {/* 3 LARGE ARENA CARDS - FISH 🐟, SHARK 🦈, WHALE 🐋 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {Object.entries(ARENA_TIERS).map(([tierId, tier]) => {
                  const canAfford = userSlap >= tier.cost;
                  const tierStyles = {
                    fish: {
                      gradient: 'from-cyan-600 via-blue-500 to-cyan-700',
                      border: 'border-cyan-400',
                      glow: 'shadow-[0_0_40px_rgba(34,211,238,0.6)]',
                      icon: '🐟',
                      label: 'FISH',
                      sublabel: 'Beginner Pool',
                      usdLabel: '$1.00'
                    },
                    shark: {
                      gradient: 'from-purple-600 via-pink-500 to-purple-700',
                      border: 'border-purple-400',
                      glow: 'shadow-[0_0_40px_rgba(168,85,247,0.6)]',
                      icon: '🦈',
                      label: 'SHARK',
                      sublabel: 'Advanced Waters',
                      usdLabel: '$5.00'
                    },
                    whale: {
                      gradient: 'from-yellow-500 via-orange-500 to-amber-600',
                      border: 'border-yellow-400',
                      glow: 'shadow-[0_0_40px_rgba(234,179,8,0.6)]',
                      icon: '🐋',
                      label: 'WHALE',
                      sublabel: 'High Roller Ocean',
                      usdLabel: '$20.00'
                    }
                  };
                  const style = tierStyles[tierId];
                  
                  return (
                    <motion.button
                      key={tierId}
                      onClick={() => {
                        if (canAfford) {
                          playSelectSound();
                          triggerHaptic();
                          setSelectedTier(tierId);
                          // AUTO-START BATTLE ON CLICK
                          setTimeout(() => handleCpuBattle(), 100);
                        } else {
                          toast.error(`Need ${tier.cost} $SLAP for ${tier.name}!`, {
                            description: 'Get more $SLAP from the Wallet'
                          });
                        }
                      }}
                      disabled={!canAfford}
                      className={`relative overflow-hidden rounded-3xl transition-all duration-300 ${
                        canAfford
                          ? `bg-gradient-to-br ${style.gradient} ${style.border} border-4 ${style.glow} hover:scale-105 cursor-pointer`
                          : 'bg-gray-800/80 border-4 border-gray-600 opacity-50 cursor-not-allowed'
                      }`}
                      style={{ aspectRatio: '3/4' }}
                      whileHover={canAfford ? { y: -10 } : {}}
                      whileTap={canAfford ? { scale: 0.95 } : {}}
                      data-testid={`arena-card-${tierId}`}
                    >
                      {/* "GET $SLAP" overlay for unaffordable tiers */}
                      {!canAfford && (
                        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/70 rounded-3xl">
                          <span className="text-4xl mb-2">💰</span>
                          <p className="font-anton text-xl text-white">NEED ${tier.cost.toLocaleString()} $SLAP</p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/wallet');
                            }}
                            className="mt-3 px-4 py-2 bg-green-500 hover:bg-green-400 text-white font-bold rounded-lg text-sm"
                          >
                            GET $SLAP →
                          </button>
                        </div>
                      )}
                      {/* Card Content */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 md:p-6">
                        {/* Icon - Larger on desktop */}
                        <span className="text-5xl md:text-7xl mb-2 drop-shadow-lg">{style.icon}</span>
                        
                        {/* TIER NAME - e.g. "FISH" */}
                        <h3 className="font-anton text-2xl md:text-4xl text-white tracking-wider"
                          style={{ textShadow: '0 0 20px rgba(255,255,255,0.5)' }}
                        >
                          {style.label}
                        </h3>
                        
                        {/* Subtitle */}
                        <p className="text-white/60 text-xs md:text-sm mb-2">{style.sublabel}</p>
                        
                        {/* USD Price - Big & Prominent */}
                        <div className="bg-black/40 rounded-xl px-4 py-2 mb-3">
                          <p className="font-anton text-2xl md:text-3xl text-yellow-400">{style.usdLabel}</p>
                          <p className="text-white/50 text-xs">{tier.cost.toLocaleString()} $SLAP</p>
                        </div>
                        
                        {/* 1v1 Label */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-[2px] bg-white/30"></div>
                          <p className="font-bold text-sm text-white/80 tracking-widest">1v1 DUEL</p>
                          <div className="w-8 h-[2px] bg-white/30"></div>
                        </div>
                        
                        {/* WIN Amount - Highlight with green */}
                        <div className="bg-green-500/20 border-2 border-green-500/50 rounded-xl px-4 py-2 w-full">
                          <p className="text-green-300 text-[10px] uppercase tracking-wide text-center">🏆 WINNER TAKES</p>
                          <p className="font-anton text-xl md:text-2xl text-green-400 text-center">
                            {tier.reward.toLocaleString()} $SLAP
                          </p>
                          <p className="text-green-500/60 text-[10px] text-center">(-10% House Fee)</p>
                        </div>
                        
                        {/* Click to Play */}
                        {canAfford && (
                          <motion.p 
                            className="mt-3 text-white font-bold text-sm uppercase tracking-wider bg-white/10 px-4 py-2 rounded-full"
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          >
                            ⚡ CLICK TO PLAY
                          </motion.p>
                        )}
                      </div>
                      
                      {/* Locked Overlay */}
                      {!canAfford && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70">
                          <span className="text-4xl mb-2">🔒</span>
                          <span className="text-red-400 font-bold">NEED ${tier.cost.toLocaleString()} $SLAP</span>
                        </div>
                      )}
                      
                      {/* Shine Effect */}
                      {canAfford && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          initial={{ x: '-100%' }}
                          animate={{ x: '200%' }}
                          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                        />
                      )}
                    </motion.button>
                  );
                })}
              </div>
              
              {/* Balance Display */}
              <div className="flex justify-center items-center gap-4 mt-6">
                <span className="text-white/60">Your Balance:</span>
                <span className="font-anton text-2xl text-yellow-400">{userSlap.toLocaleString()} $SLAP</span>
              </div>
            </motion.div>
          )}

          {/* Matchmaking Status */}
          {isMatchmaking && (
            <motion.div
              className="flex flex-col items-center gap-4 mb-8 py-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              >
                <Loader2 className={`w-16 h-16 ${isProcessingPayment ? 'text-green-400' : 'text-yellow-400'}`} />
              </motion.div>
              
              {/* Payment Progress */}
              {isProcessingPayment ? (
                <div className="text-center">
                  <p className="font-anton text-2xl text-green-400">PROCESSING PAYMENT...</p>
                  <p className="font-space text-white/70 mt-2">
                    {matchmakingStatus?.message || 'Connecting to Solana...'}
                  </p>
                  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl max-w-sm mx-auto">
                    <p className="text-green-400 text-sm">
                      💰 Real on-chain transfer to Treasury
                    </p>
                    <p className="text-white/50 text-xs mt-1">
                      {TREASURY_WALLET.slice(0, 12)}...{TREASURY_WALLET.slice(-4)}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="font-anton text-2xl text-white">
                  {matchmakingStatus?.message || 'PREPARING BATTLE...'}
                </p>
              )}
              
              {!isProcessingPayment && (
                <Button onClick={cancelMatchmaking} variant="outline" className="text-red-400 border-red-400">
                  Cancel
                </Button>
              )}
            </motion.div>
          )}

          {/* Open Battles */}
          <div>
            <h2 className="font-anton text-2xl text-white mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-400" />
              OPEN BATTLES ({availableBattles.length})
            </h2>
            {availableBattles.length === 0 ? (
              <div className="text-center py-12 bg-black/20 rounded-lg border-2 border-white/10">
                <p className="font-space text-white/40">No battles available. Start a match!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableBattles.map((battle) => (
                  <motion.div
                    key={battle.battle_id}
                    className="bg-black/40 border-2 border-neon-pink/30 rounded-lg p-6 hover:border-neon-pink transition-all"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <span className="font-anton text-2xl text-neon-pink">3-ROUND BATTLE</span>
                      <Swords className="w-8 h-8 text-red-500" />
                    </div>
                    <Button className="w-full bg-neon-green hover:bg-neon-green/80 text-black font-anton uppercase">
                      JOIN BATTLE
                    </Button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PvPArena;
